import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import '../../styles/Admin.scss';

import rightArrow1 from '../../assets/rightLast.svg';
import rightArrow2 from '../../assets/right.svg';
import leftArrow1 from '../../assets/leftLast.svg';
import leftArrow2 from '../../assets/left.svg';
import iconDelete from '../../assets/icon_delete.svg';
import iconView from '../../assets/Icon_view.svg'; // 상세보기 아이콘 적용 필요!
import AppSpinner from '../../components/AppSpinner';

interface CounselingSession {
  _id: string;
  user: {
    _id: string;
    displayName: string;
    userId: string;
    email: string;
  };
  messages: {
    sender: 'user' | 'bot';
    content: string;
    timestamp: string;
  }[];
  updatedAt: string;
  createdAt: string;
}

const AdminCounselingHistoryPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate(); // Hook 사용
  const [sessions, setSessions] = useState<CounselingSession[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [keyword, setKeyword] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [totalSessions, setTotalSessions] = useState<number>(0);
  const itemsPerPage = 20;

  const [selectedSessionIds, setSelectedSessionIds] = useState<string[]>([]);

  const fetchSessions = async (page: number) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params: any = {
        page,
        limit: itemsPerPage,
      };

      if (keyword) params.keyword = keyword;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;

      const response = await axios.get('/api/admin/counseling-sessions', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params,
      });

      setSessions(response.data.sessions);
      setTotalPages(response.data.totalPages);
      setTotalSessions(response.data.totalSessions);
      setCurrentPage(response.data.currentPage);
      setSelectedSessionIds([]);
    } catch (err) {
      console.error(err);
      setError('상담 내역을 불러오는 데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchSessions(1);
    }
  }, [isAuthenticated]);

  const handleSearch = () => {
    setCurrentPage(1);
    fetchSessions(1);
  };

  const handleReset = () => {
    setKeyword('');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    fetchSessions(1);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      fetchSessions(page);
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const allIds = sessions.map(session => session._id);
      setSelectedSessionIds(allIds);
    } else {
      setSelectedSessionIds([]);
    }
  };

  const handleSelectSession = (id: string) => {
    setSelectedSessionIds(prev =>
      prev.includes(id) ? prev.filter(sessionId => sessionId !== id) : [...prev, id]
    );
  };

  const handleDeleteSelected = async () => {
    if (selectedSessionIds.length === 0) {
      alert('삭제할 항목을 선택해주세요.');
      return;
    }

    if (!window.confirm(`선택한 ${selectedSessionIds.length}개의 상담 내역을 삭제하시겠습니까?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await Promise.all(
        selectedSessionIds.map(id =>
          axios.delete(`/api/admin/counseling-sessions/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          })
        )
      );
      alert('삭제되었습니다.');
      fetchSessions(currentPage);
    } catch (err) {
      console.error(err);
      alert('삭제 중 오류가 발생했습니다.');
    }
  };

  const isAllSelected =
    sessions.length > 0 && sessions.every(s => selectedSessionIds.includes(s._id));

  const getLastMessage = (session: CounselingSession) => {
    if (session.messages && session.messages.length > 0) {
      const lastMsg = session.messages[session.messages.length - 1];
      return `${lastMsg.sender === 'bot' ? '[리본]' : '[사용자]'} ${lastMsg.content}`;
    }
    return '(대화 내용 없음)';
  };

  if (loading && sessions.length === 0) return <AppSpinner fullscreen tip="Loading..." />;

  return (
    <div className="admin-page">
      <h1>상담내역 관리</h1>

      <div className="search-filter-container">
        <div className="filter-group">
          <label>기간</label>
          <div className="date-range-group">
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
            <span> ~ </span>
            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
          </div>
        </div>

        <div className="filter-group">
          <label>검색</label>
          <div className="search-input-group">
            <input
              type="text"
              placeholder="내용, 사용자 이름/ID 검색"
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
            />
          </div>
        </div>

        <div className="button-group">
          <button className="btn-search" onClick={handleSearch}>
            검색
          </button>
          <button className="btn-reset" onClick={handleReset}>
            초기화
          </button>
        </div>
      </div>

      <div className="list-info">
        총 상담 <span className="count">{totalSessions}</span> | 페이지{' '}
        <span className="page">
          {currentPage} / {totalPages || 1}
        </span>
      </div>

      <div className="table-scroll">
        <table className="table">
          <colgroup>
            <col width="50px" />
            <col width="6%" />
            <col width="20%" />
            <col width="12%" />
            <col width="12%" />
            <col width="8%" />
            <col width="29%" />
            <col width="8%" />
          </colgroup>
          <thead>
            <tr>
              <th>
                <label className="custom-checkbox">
                  <input type="checkbox" checked={isAllSelected} onChange={handleSelectAll} />
                  <span className="checkmark"></span>
                </label>
              </th>
              <th>번호</th>
              <th>최근 상담일시</th>
              <th>사용자</th>
              <th>아이디</th>
              <th>대화 수</th>
              <th>최근 대화 내용</th>
              <th>상세</th>
            </tr>
          </thead>
          <tbody>
            {sessions.length > 0 ? (
              sessions.map((session, index) => (
                <tr key={session._id}>
                  <td>
                    <label className="custom-checkbox">
                      <input
                        type="checkbox"
                        checked={selectedSessionIds.includes(session._id)}
                        onChange={() => handleSelectSession(session._id)}
                      />
                      <span className="checkmark"></span>
                    </label>
                  </td>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>
                    {new Date(session.updatedAt).toLocaleDateString()}{' '}
                    {new Date(session.updatedAt).toLocaleTimeString([], {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </td>
                  <td>{session.user ? session.user.displayName : '(알 수 없음)'}</td>
                  <td>{session.user ? session.user.userId : '-'}</td>
                  <td>{session.messages.length}</td>
                  <td style={{ textAlign: 'left' }}>
                    <div className="ellipsis-1"
                      title={getLastMessage(session)}
                    >
                      {getLastMessage(session)}
                    </div>
                  </td>
                  <td>
                    <button
                      className="btn-icon view m-auto" title="상세보기"
                      onClick={() => navigate(`/admin/counseling-history/${session._id}`)}
                    >
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="no-data">
                  데이터가 없습니다.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="pagination-container">
        <div className="pagination">
            <button onClick={() => handlePageChange(1)} disabled={currentPage === 1}>
              <img src={leftArrow1} alt="처음" />
            </button>
            <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
              <img src={leftArrow2} alt="이전" />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
              if (
                totalPages > 10 &&
                (page < currentPage - 2 || page > currentPage + 2) &&
                page !== 1 &&
                page !== totalPages
              ) {
                return null;
              }
              return (
                <button
                  key={page}
                  className={currentPage === page ? 'active' : ''}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              <img src={rightArrow2} alt="다음" />
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
            >
              <img src={rightArrow1} alt="끝" />
            </button>
          </div>
        <div className="bottom-actions">
          <button className="btn-delete-selection" onClick={handleDeleteSelected}>
            <img src={iconDelete} alt="삭제" />
            삭제
          </button>      
        </div>
      </div>
    </div>
  );
};

export default AdminCounselingHistoryPage;
